import { type ReactNode } from 'react';
import { Link } from 'react-router';

import { cn } from '@mds/shared/lib/utils';
import { useReducedMotion } from '@mds/shared/hooks/use-reduced-motion';
import { Button } from '@mds/shared/ui/shadcn/button';

/**
 * The full-bleed opener.
 *
 * Every slot is data rather than a node, including the call to action. That is
 * the one worth explaining, because `action?: ReactNode` looks more flexible
 * and is in fact worse here: the hero is the only dark surface on the page, so
 * a button handed in from outside arrives styled for a light one. Taking a
 * label and a destination instead means the hero renders its own button, on the
 * ground it knows it has, and the call site cannot get it wrong.
 *
 * The colours of that button are no longer this file's problem: the section
 * marks itself `data-ground="panel"` and Tier 3 repoints the green and the
 * focus ring for everything inside it. This used to be a hand-written class on
 * the button — correct, and correct only here, which is how the footer ended up
 * avoiding `Button` altogether rather than inheriting the same fix.
 *
 * Three other things here are load-bearing and easy to lose in a later edit:
 *
 * 1. The media is decorative, always. The heading carries the meaning, so the
 *    image takes an empty alt and the video is hidden from the accessibility
 *    tree. A background that has to be described is not a background.
 *
 * 2. The scrim is not styling. It is the only reason white text on a
 *    photograph is readable, and `--hero-scrim` is set at the depth that holds
 *    up against the brightest frame the media could contain. Thinning it to
 *    show more of the picture breaks the contrast the tokens promise.
 *
 * 3. The section pulls itself up under the header by exactly the header's own
 *    height, then pads the same amount back in. That is what puts the nav on
 *    the picture rather than on a strip of page above it, and it is why the
 *    content still sits centred in what you can actually see.
 */
export default function Hero({
  eyebrow,
  title,
  description,
  action,
  image,
  video,
  className,
}: {
  /** The small line above the title — a place, a category. Uppercased here. */
  eyebrow?: ReactNode;
  /** The display line. The only part that is not optional. */
  title: ReactNode;
  /** The sentence under the title: what this place is, in one breath. */
  description?: ReactNode;
  /**
   * The one thing to do from here. A label and an in-app route — the hero
   * supplies the button, because it is the only thing that knows what ground
   * the button is standing on.
   */
  action?: { label: ReactNode; to: string };
  /**
   * A still. Doubles as the video's poster and as the fallback for anyone the
   * video will not play for — so a hero with a video should give this too.
   * Any URL: a file in `public/`, or one the API hands over later.
   */
  image?: string;
  /**
   * An optional looping background video. Muted, decorative, and never
   * autoplayed for someone who has asked their OS for reduced motion.
   */
  video?: string;
  className?: string;
}) {
  const reducedMotion = useReducedMotion();
  const showVideo = Boolean(video) && !reducedMotion;

  return (
    <section
      /* Everything in here is standing on the scrim, so the green and the focus
       * ring take their dark-ground steps. One mark on the section rather than a
       * patch on each control — see the `[data-ground='panel']` block in
       * components.css. */
      data-ground="panel"
      className={cn(
        'relative isolate -mt-header flex min-h-svh flex-col items-center justify-center',
        'overflow-hidden bg-(--hero-ground) px-gutter pt-header pb-section text-center',
        className,
      )}
    >
      {/* Negative z puts both layers above the section's own background and
          below its content, without either one having to be positioned
          against the copy. */}
      {showVideo ? (
        <video
          className="absolute inset-0 -z-10 size-full object-cover"
          src={video}
          poster={image}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden
        />
      ) : image ? (
        <img
          className="absolute inset-0 -z-10 size-full object-cover"
          src={image}
          alt=""
          aria-hidden
        />
      ) : null}

      <div aria-hidden className="absolute inset-0 -z-10 bg-(--hero-scrim)" />

      <div className="flex max-w-prose flex-col items-center gap-6">
        {eyebrow && (
          <p className="font-mono text-xs font-medium text-(--hero-eyebrow-fg) uppercase">
            {eyebrow}
          </p>
        )}

        <h1 className="font-display text-4xl text-balance text-(--hero-fg) sm:text-5xl">{title}</h1>

        {description && <p className="text-body-lg text-pretty text-(--hero-body-fg)">{description}</p>}

        {/* No focus-ring override on the button. The section is marked
            `data-ground`, so `--ring` is already `--focus-on-panel` in here —
            the default mint 700 at 50% is 1.6:1 on the scrim and was being
            patched by hand at this one call site before the ground itself could
            say so. */}
        {/* The light ground's green rather than the bright mint `data-ground`
            would give it: the `btn-brand` pairing states its own white label,
            so the button reads the same here as on the page below. */}
        {action && (
          <Button
            size="lg"
            asChild
            className="mt-2 bg-btn-brand-bg text-btn-brand-fg hover:bg-btn-brand-hover-bg hover:text-btn-brand-hover-fg"
          >
            <Link to={action.to}>{action.label}</Link>
          </Button>
        )}
      </div>
    </section>
  );
}
